import type { DashboardDefinition } from "@/components/daylight-insight/api/types";
import { pillarKey } from "./pillars";

/* ============================================================================
   DASHBOARD SPEC · the Operating View's render contract

   One granted dashboard = one tab = one page. Everything that used to be
   hard-coded per pillar in the frontend — which filters the bar shows, which
   branch list feeds Branch, which detail table sits under the KPIs, which
   extra cards the page carries — is described HERE, and the server may supply
   all of it (`config.dashboards`). The drill's views follow the filters: Trend,
   then one tab per primary filter, then the non-primary ones under "More ▾".

   Adding a dashboard is therefore a config change: the SP returns a row for it,
   the tab appears, and the page renders with the filters/table/drill views the
   row names. Nothing in this file needs a new entry for that to work — the
   `DEFAULTS` below are only what a dashboard gets when the server says nothing.

   The filter BAR is open-ended too: a filter's dropdown options are the config
   node its `key` names (see {@link filterOptionNode}), and its selection goes
   to the metrics SP's `@Filters` JSON under that same key.
   ========================================================================== */

export interface FilterSpec {
  /** The config's filter key, verbatim. Names the config node its dropdown
   *  options come from, the `@Filters` JSON property its selection is sent
   *  under, and the drill's summary dimension (`@SummarizeBy`). */
  key: string;
  /** Display name from the config ("Branch") — the control's label. */
  name?: string;
  /** Prefixes the options node: `{ key: "branch", source: "productivity" }`
   *  reads `productivityBranch` instead of `branch` (Workforce: only branches
   *  that report productivity may be picked). */
  source?: string;
  /** Primary filters sit in the filter bar as a dropdown AND are a drill tab
   *  ("By …"). Non-primary ones are the bar's "+ Focus" dimensions (a chip per
   *  picked member) and a drill "More ▾" breakdown. Both are sent the same way.
   *  Missing = primary. */
  isPrimary?: boolean;
}

/** Whether a filter is a filter-bar dropdown — see {@link FilterSpec.isPrimary}. */
export const isPrimaryFilter = (f: FilterSpec): boolean => f.isPrimary !== false;

/** The filters a page renders and sends, and the drill's tabs after Trend:
 *  the primary ones, in order. */
export const primaryFilters = (filters: FilterSpec[]): FilterSpec[] => filters.filter(isPrimaryFilter);

/** The rest: the drill's "More ▾" breakdowns, in order. */
export const secondaryFilters = (filters: FilterSpec[]): FilterSpec[] =>
  filters.filter((f) => !isPrimaryFilter(f));

/** A filter's wording: the config `name`, else its key. */
export const filterLabel = (f: FilterSpec): string => f.name?.trim() || f.key;

/** English plural of a label's LAST word, keeping its case: "Branch" →
 *  "Branches", "Payor" → "Payors", "Referral Source" → "Referral Sources",
 *  "Facility" → "Facilities". */
export const pluralize = (label: string): string => {
  const m = /^(.*?)([A-Za-z]+)$/.exec(label.trim());
  if (!m) return label;
  const [, head, word] = m;
  const upper = word.length > 1 && word === word.toUpperCase();
  const lower = word.toLowerCase();
  let plural: string;
  if (/[^aeiou]y$/.test(lower)) plural = `${word.slice(0, -1)}ies`;
  else if (/(s|x|z|ch|sh)$/.test(lower)) plural = `${word}es`;
  else plural = `${word}s`;
  return head + (upper ? plural.toUpperCase() : plural);
};

/** A filter's "nothing narrowed" wording: "All Branches", "All Payors". */
export const filterAllLabel = (f: FilterSpec): string => `All ${pluralize(filterLabel(f))}`;

/** The `dashboard/config` node holding a filter's dropdown options: the key
 *  itself, or `source + Key` when the filter carries a `source`. */
export const filterOptionNode = (f: Pick<FilterSpec, "key" | "source">): string =>
  f.source ? `${f.source}${f.key.charAt(0).toUpperCase()}${f.key.slice(1)}` : f.key;

/**
 * TRANSITIONAL. Saved filter preferences may still carry one named parameter
 * per filter (the pre-`@Filters` shape). This maps a config filter key to that
 * parameter so those selections can be restored; a key missing here simply
 * isn't read. Delete once every saved row is keyed by the config filter key.
 */
export const LEGACY_FILTER_PARAMS = {
  branch: "branchSystemIDs",
  payor: "payorTypeSystemIDs",
  discipline: "disciplines",
  jobTitle: "jobTitles",
  referralSourceType: "referralSubTypes",
  referralSource: "referralSourceNames",
  rep: "repRosterSystemIDs",
  salesRep: "repRosterSystemIDs",
} as const;

/** A filter's dimension in `GetMetricSummaryForDateRange`'s `@SummarizeBy`:
 *  the config's own filter key, verbatim ("salesRep", "referralSource", …). */
export const summarizeByFor = (key: FilterSpec["key"]): string => key;

/** A detail table under the KPIs — one per summary SP that backs a table. */
export const TABLE_KINDS = ["branch", "clinician"] as const;
export type TableKind = (typeof TABLE_KINDS)[number];

/** A slider over one of the table's OWN columns. Client-side: the clinician SP
 *  takes no such parameter, so these narrow the rows already on screen. The
 *  column is matched by name against what the SP returned; when it isn't there,
 *  the slider isn't rendered (a filter must never empty a table over a column
 *  that doesn't exist). */
export interface RowFilterSpec {
  /** The column's name as the SP spells it, e.g. "% Eligible ETA Text Sent". */
  column: string;
  /** Slider label. Defaults to `column`. */
  label?: string;
  /** Fixed domain, e.g. `[0, 100]` for a percentage. Omitted → read off the
   *  rows, which is the only sane option for an unbounded count. */
  domain?: [number, number];
  /** Value suffix on the handles: "%" or a plain number. */
  unit?: "percent" | "count";
}

export interface TableSpec {
  /** Stable id within the dashboard (React key, export registry). */
  key: string;
  kind: TableKind;
  /** Card heading and export sheet name. Defaults per kind ("By Branch"). */
  title?: string;
  rowFilters?: RowFilterSpec[];
}

/** An extra card between the KPI row and the detail table. Today there is one
 *  kind: the by-Discipline breakdown (Snapshot | Trend) Patient Notifications
 *  carries, plotting one metric across disciplines. */
export interface SectionSpec {
  type: "disciplineBreakdown";
  /** The metric the card plots and a bar click drills into. */
  metricCode: string;
}

/** Which page shell renders the dashboard: the ordinary filtered metrics page,
 *  or the unfiltered as-of-now snapshot (Current State). */
export type DashboardPage = "metrics" | "snapshot";

/** A fully resolved dashboard — server spec merged over the defaults. This is
 *  what the Operating View renders from; nothing downstream branches on a
 *  dashboard's identity any more. */
export interface DashboardSpec {
  dashboardId: number | string;
  /** The key the UI routes on (`store.pillar`). */
  key: string;
  /** Tab label and page title. */
  label: string;
  description: string | null;
  page: DashboardPage;
  /** The "Drill by …" chip. Null → the page falls back to the seeded grain. */
  drillGrain: string | null;
  filters: FilterSpec[];
  /** Detail tables, rendered in order. Empty = the dashboard has none. */
  tables: TableSpec[];
  sections: SectionSpec[];
  /** Service-line slugs the tab appears under. Empty = every line. */
  serviceLines: string[];
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

/** What a dashboard renders as when the server describes none of it: the shape
 *  every branch-grain dashboard shares. A brand-new dashboard the server sends
 *  with nothing but an id and a label lands here and is fully usable. */
export const DEFAULT_SPEC: Omit<DashboardSpec, "dashboardId" | "key" | "label"> = {
  description: null,
  page: "metrics",
  drillGrain: null,
  filters: [{ key: "branch" }, { key: "payor" }],
  tables: [{ key: "branch", kind: "branch" }],
  sections: [],
  serviceLines: [],
};

/**
 * The per-dashboard behaviour this build shipped with, keyed by pillar key.
 *
 * TRANSITIONAL. It exists so the Operating View is byte-for-byte unchanged
 * until `dashboard/config` starts carrying `dashboards[]` — every field here is
 * overridden by the server the moment it sends one. Once the SP is authoritative
 * this map can be deleted, and a dashboard missing from it is NOT a problem
 * today either: it falls back to {@link DEFAULT_SPEC}.
 */
export const LEGACY_SPECS: Record<string, Partial<DashboardSpec>> = {
  operations: {},
  sales: {
    filters: [
      { key: "branch" },
      { key: "payor" },
      { key: "referralSource" },
      { key: "referralSourceType" },
      { key: "rep" },
    ],
  },
  revenue: {},
  clinical: { filters: [{ key: "branch" }, { key: "discipline" }] },
  // Workforce picks from the productivity branches, narrows by job title, and
  // shows the ordinary By-Branch table.
  workforce: {
    filters: [{ key: "branch", source: "productivity" }, { key: "payor" }, { key: "jobTitle" }],
  },
  orders: {},
  billing: {},
  financial: { filters: [{ key: "branch" }, { key: "payor" }, { key: "aging" }] },
  // The one clinician-grain dashboard: a by-Discipline card over the ETA
  // attainment metric, then the By-Clinician table with its two sliders.
  "patient-notifications": {
    filters: [{ key: "branch" }, { key: "payor" }, { key: "discipline" }],
    tables: [
      {
        key: "clinician",
        kind: "clinician",
        rowFilters: [
          { column: "% Eligible ETA Text Sent", domain: [0, 100], unit: "percent" },
          { column: "Visits Completed", unit: "count" },
        ],
      },
    ],
    sections: [{ type: "disciplineBreakdown", metricCode: "ETA_PercentEligibleTextsSent" }],
  },
  experience: {},
  quality: {},
  hospitalization: {},
  visits: {
    filters: [{ key: "branch" }, { key: "payor" }, { key: "discipline" }],
  },
  // Current State: the snapshot shell — no filter bar, no detail table.
  snapshot: { page: "snapshot", filters: [], tables: [] },
};

// ─── Resolution ───────────────────────────────────────────────────────────────

/** Server filters → specs, kept whatever their key. A bare string is sugar for
 *  `{ key }` (primary). */
const toFilterSpecs = (raw: DashboardDefinition["filters"]): FilterSpec[] | null => {
  if (!Array.isArray(raw)) return null;
  const out: FilterSpec[] = [];
  for (const f of raw) {
    if (typeof f === "string") {
      if (f.trim()) out.push({ key: f.trim() });
      continue;
    }
    if (!f || typeof f !== "object") continue;
    const key = String(f.key ?? "").trim();
    if (key) {
      out.push({
        key,
        ...(f.name ? { name: f.name } : {}),
        ...(typeof f.source === "string" && f.source.trim() ? { source: f.source.trim() } : {}),
        ...(f.isPrimary === false ? { isPrimary: false } : {}),
      });
    }
  }
  return out;
};

const isTableKind = (v: unknown): v is TableKind =>
  (TABLE_KINDS as readonly string[]).includes(String(v));

/** Server tables → specs. Accepts the list form (`[{ kind, … }, …]`) and, for
 *  older rows, a single object or bare kind string. `"none"` and `[]` both mean
 *  "no table"; an entry of a kind no table exists for is dropped. Missing or
 *  malformed → null, i.e. "not specified" (the defaults apply). */
const toTableSpecs = (raw: DashboardDefinition["table"]): TableSpec[] | null => {
  if (raw === null || raw === undefined) return null;
  const list = Array.isArray(raw) ? raw : [raw];
  const out: TableSpec[] = [];
  const keys = new Set<string>();
  for (const t of list) {
    const obj = typeof t === "string" ? { kind: t } : t;
    if (!obj || typeof obj !== "object" || !isTableKind(obj.kind)) continue;
    // Keys must be unique within the page — they key the React list and the
    // export registry — so a repeated one gets a positional suffix.
    let key = String(obj.key ?? "").trim() || obj.kind;
    if (keys.has(key)) key = `${key}-${out.length}`;
    keys.add(key);
    const rowFilters = Array.isArray(obj.rowFilters)
      ? obj.rowFilters.filter((r): r is RowFilterSpec => !!r && typeof r.column === "string" && r.column !== "")
      : [];
    out.push({
      key,
      kind: obj.kind,
      ...(obj.title ? { title: obj.title } : {}),
      ...(rowFilters.length ? { rowFilters } : {}),
    });
  }
  return out;
};

/** Service-line slugs, lower-cased. Missing/invalid → null (every line). */
const toServiceLines = (raw: DashboardDefinition["serviceLines"]): string[] | null => {
  if (!Array.isArray(raw)) return null;
  return raw.map((v) => String(v ?? "").trim().toLowerCase()).filter(Boolean);
};

const toSectionSpecs = (raw: DashboardDefinition["sections"]): SectionSpec[] | null => {
  if (!Array.isArray(raw)) return null;
  return raw.filter(
    (sec): sec is SectionSpec =>
      !!sec && sec.type === "disciplineBreakdown" && typeof sec.metricCode === "string" && sec.metricCode !== "",
  );
};

/**
 * The key the UI routes on.
 *
 * The server's own `key` wins — that is the stable identifier a new dashboard
 * should ship with. Without one the legacy label heuristic stands in, so today's
 * dashboards keep the keys they have; and when THAT yields nothing recognisable
 * the id does, which is what makes an unrecognised dashboard routable at all
 * (two of them would otherwise both answer to "other").
 *
 * Lower-cased: other surfaces look a dashboard up by `pillarKey(label)` (a
 * drill into another dashboard's metric, All Metrics' details), which is always
 * lower-case — a config key of "Operations" must still answer to "operations".
 */
export const dashboardKeyFor = (d: { key?: string | null; label: string; dashboardId: number | string }): string => {
  const explicit = String(d.key ?? "").trim().toLowerCase();
  if (explicit) return explicit;
  const legacy = pillarKey(d.label);
  return legacy === "other" ? `dashboard-${d.dashboardId}` : legacy;
};

/** One server definition → the spec the page renders. Anything the row leaves
 *  unsaid falls back to this build's defaults for that key. */
export function resolveDashboardSpec(definition: DashboardDefinition): DashboardSpec {
  const label = definition.label?.trim() || `Dashboard ${definition.dashboardId}`;
  const key = dashboardKeyFor({ key: definition.key, label, dashboardId: definition.dashboardId });
  // Legacy defaults are keyed by the pillar key this build knows, which is the
  // resolved key for every dashboard that has one.
  const legacy = LEGACY_SPECS[key] ?? {};
  const base: DashboardSpec = {
    ...DEFAULT_SPEC,
    ...legacy,
    dashboardId: definition.dashboardId,
    key,
    label,
  };

  const filters = toFilterSpecs(definition.filters);
  const tables = toTableSpecs(definition.table);
  const sections = toSectionSpecs(definition.sections);
  const serviceLines = toServiceLines(definition.serviceLines);
  return {
    ...base,
    description: definition.description ?? base.description,
    page: definition.page === "snapshot" || definition.page === "metrics" ? definition.page : base.page,
    drillGrain: definition.drillGrain ?? base.drillGrain,
    // A server-sent EMPTY list is meaningful ("this dashboard has no filters"),
    // so only a missing/invalid one falls back.
    filters: filters ?? base.filters,
    // Same rule as filters: a server-sent EMPTY list means "no tables".
    tables: tables ?? base.tables,
    sections: sections ?? base.sections,
    serviceLines: serviceLines ?? base.serviceLines,
  };
}

/**
 * The Operating View's tabs, in the order the server sent them. Callers pass
 * the selected role's rows only (`config.dashboards` filtered on `roleId`).
 *
 * Every granted dashboard gets one — there is no registry of renderable pages
 * to pass first. A dashboard this build has never heard of renders from
 * {@link DEFAULT_SPEC} (KPIs, branch/payor filters, By-Branch table, trend +
 * branch + payor drill views), which is exactly the point: shipping one is a
 * config change.
 *
 * `service` ("hh" | "hos") drops the dashboards whose spec names service lines
 * that don't include it. A spec naming none appears under every line.
 */
export function resolveDashboardSpecs(
  definitions: DashboardDefinition[],
  service?: string,
): DashboardSpec[] {
  const specs: DashboardSpec[] = [];
  const seen = new Set<string>();
  for (const d of definitions) {
    const spec = resolveDashboardSpec(d);
    if (service && spec.serviceLines.length && !spec.serviceLines.includes(service)) continue;
    // The same dashboard granted twice, or two rows resolving to one key.
    if (seen.has(spec.key)) continue;
    seen.add(spec.key);
    specs.push(spec);
  }
  return specs;
}
